/**
 * Audio Service for playing base64-encoded audio content
 * Handles audio playback for ABAIR speech synthesis
 */

const audioServiceRegistry = new Set<AudioService>();
let activeAudioService: AudioService | null = null;
let playbackInterruptionVersion = 0;
let activeSynthesisController: AbortController | null = null;
let activeSynthesisKind: 'foreground' | 'prefetch' | null = null;
export const STT_RECORDING_STARTED_EVENT = 'stt-recording-started';
export const TTS_ACTIVITY_STARTED_EVENT = 'tts-activity-started';

interface AudioSynthesisRequest {
  controller: AbortController;
  interruptionVersion: number;
}

export function getAudioPlaybackInterruptionVersion(): number {
  return playbackInterruptionVersion;
}

export function stopAllAudioPlayback(): void {
  playbackInterruptionVersion += 1;
  activeSynthesisController?.abort();
  activeSynthesisController = null;
  activeSynthesisKind = null;

  if (typeof window !== 'undefined') {
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    } catch (error) {
      console.warn('Failed to cancel speech synthesis:', error);
    }

    try {
      const mediaElements = document.querySelectorAll<HTMLMediaElement>('audio, video');
      mediaElements.forEach((element) => {
        try {
          element.pause();
          element.currentTime = 0;
        } catch {
          // Ignore individual media element errors while stopping global playback.
        }
      });
    } catch (error) {
      console.warn('Failed to stop media elements:', error);
    }
  }

  for (const service of audioServiceRegistry) {
    service.stop();
  }
  activeAudioService = null;
}

function announceTtsActivity(messageId: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<string>(TTS_ACTIVITY_STARTED_EVENT, { detail: messageId }));
  }
}

export function beginAudioPlayback(messageId: string): number {
  stopAllAudioPlayback();
  announceTtsActivity(messageId);
  return playbackInterruptionVersion;
}

export function beginAudioSynthesis(messageId: string): AudioSynthesisRequest {
  const interruptionVersion = beginAudioPlayback(messageId);
  const controller = new AbortController();
  activeSynthesisController = controller;
  activeSynthesisKind = 'foreground';
  return { controller, interruptionVersion };
}

export function beginAudioPrefetch(): AudioSynthesisRequest | null {
  if (activeSynthesisController && activeSynthesisKind === 'foreground') {
    return null;
  }

  activeSynthesisController?.abort();
  const controller = new AbortController();
  activeSynthesisController = controller;
  activeSynthesisKind = 'prefetch';
  return { controller, interruptionVersion: playbackInterruptionVersion };
}

export function completeAudioSynthesis(controller: AbortController): void {
  if (activeSynthesisController === controller) {
    activeSynthesisController = null;
    activeSynthesisKind = null;
  }
}

export class AudioService {
  private audioElement: HTMLAudioElement | null = null;
  private currentAudioUrl: string | null = null;
  private playbackRequestVersion = 0;

  constructor() {
    audioServiceRegistry.add(this);
  }

  /**
   * Play base64-encoded audio content
   * @param base64Audio - Base64 encoded audio string
   * @returns Promise that resolves when audio starts playing
   */
  async playBase64Audio(
    base64Audio: string,
    onEnded?: () => void,
    onError?: (error: unknown) => void
  ): Promise<void> {
    const requestVersion = ++this.playbackRequestVersion;

    try {
      if (activeAudioService && activeAudioService !== this) {
        activeAudioService.stop();
      }
      activeAudioService = this;

      // Clean up previous audio
      this.cleanup();

      // Decode base64 to binary
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob and play
      const blob = new Blob([bytes], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);
      this.currentAudioUrl = audioUrl;
      
      this.audioElement = new Audio(audioUrl);
      
      // Set up event listeners
      this.audioElement.addEventListener('ended', () => {
        if (requestVersion !== this.playbackRequestVersion) {
          return;
        }
        this.cleanup();
        if (activeAudioService === this) {
          activeAudioService = null;
        }
        if (onEnded) {
          onEnded();
        }
      });
      
      this.audioElement.addEventListener('error', (e) => {
        if (requestVersion !== this.playbackRequestVersion) {
          return;
        }
        console.error('Audio playback error:', e);
        this.cleanup();
        if (activeAudioService === this) {
          activeAudioService = null;
        }
        if (onError) {
          onError(e);
        }
      });
      
      // Play the audio
      await this.audioElement.play();

      if (requestVersion !== this.playbackRequestVersion) {
        this.stop();
      }
    } catch (error) {
      if (requestVersion !== this.playbackRequestVersion) {
        return;
      }
      console.error('Error playing audio:', error);
      this.cleanup();
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  /**
   * Stop current audio playback
   */
  stop(): void {
    this.playbackRequestVersion += 1;
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
    this.cleanup();
    if (activeAudioService === this) {
      activeAudioService = null;
    }
  }

  /**
   * Check if audio is currently playing
   */
  isPlaying(): boolean {
    return this.audioElement !== null && !this.audioElement.paused;
  }

  /**
   * Get current audio duration in seconds
   */
  getDuration(): number {
    return this.audioElement?.duration || 0;
  }

  /**
   * Get current playback position in seconds
   */
  getCurrentTime(): number {
    return this.audioElement?.currentTime || 0;
  }

  /**
   * Clean up audio resources
   */
  private cleanup(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement = null;
    }
    
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
  }

  /**
   * Destructor - clean up resources
   */
  destroy(): void {
    this.stop();
    audioServiceRegistry.delete(this);
    this.cleanup();
  }
}
